import {
  HttpErrorResponse,
  HttpEvent,
  HttpHandlerFn,
  HttpInterceptorFn,
  HttpRequest,
} from '@angular/common/http';
import { inject } from '@angular/core';
import { StatusCodes } from 'http-status-codes';
import { catchError, Observable, throwError } from 'rxjs';

import { ErrorDialogData } from '@VineyardLib/data/error-dialog-data';
import { isMessageData } from '@VineyardLib/data/message-data';
import {
  ERROR_DIALOG_SERVICE_TOKEN,
  ErrorDialogWrapperService,
} from '@VineyardLib/services/error-dialog-wrapper.service';
import { LoginService } from '@VineyardLib/services/login.service';
import { convertToMessageData } from '@VineyardLib/utils/typeConversion.util';

export const httpErrorInterceptor: HttpInterceptorFn = (
  req: HttpRequest<unknown>,
  next: HttpHandlerFn
) => {
  const dialog = inject(ERROR_DIALOG_SERVICE_TOKEN);
  const login = inject(LoginService);
  return next(req).pipe(
    catchError((error: HttpErrorResponse) => {
      const command = commands.find((command) => command.valid(error));
      if (command == null) {
        return throwError(() => error);
      }
      return command.run(error, dialog, login);
    })
  );
};

/**
 * エラーコマンドのインターフェース
 */
interface ErrorCommand {
  /** 検証 */
  valid(error: HttpErrorResponse): boolean;
  /** 実行 */
  run(
    error: HttpErrorResponse,
    dialog: ErrorDialogWrapperService,
    login?: LoginService
  ): Observable<HttpEvent<any>>;
}

/**
 * IDCSとのHTTP通信におけるエラーコマンド
 */
class IdcsErrorCommand implements ErrorCommand {
  /** 検証 */
  valid = (error: HttpErrorResponse): boolean =>
    error?.error?.error_description;
  /** 実行 */
  run = (
    error: HttpErrorResponse,
    dialog: ErrorDialogWrapperService
  ): Observable<HttpEvent<any>> => {
    const data: ErrorDialogData = {
      message: convertToMessageData(error?.error?.error_description),
      timestamp: Date.now().toString(),
    };
    dialog.openErrorDialog(data);
    return throwError(() => error);
  };
}

/**
 * IdcsAdminAPIのHTTP通信におけるエラーコマンド
 */
class IdcsAdminApiErrorCommand implements ErrorCommand {
  /** 検証 */
  valid = (error: HttpErrorResponse): boolean => error?.error?.detail;
  /** 実行 */
  run = (
    error: HttpErrorResponse,
    dialog: ErrorDialogWrapperService
  ): Observable<HttpEvent<any>> => {
    const data: ErrorDialogData = {
      message: convertToMessageData(error?.error?.detail),
      timestamp: Date.now().toString(),
    };
    dialog.openErrorDialog(data);
    return throwError(() => error);
  };
}

/**
 * ServerとのHTTP通信におけるエラーコマンド
 */
class ServerErrorCommand implements ErrorCommand {
  /** 検証 */
  valid = (error: HttpErrorResponse): boolean => error?.status !== 0;
  /** 実行 */
  run = (
    error: HttpErrorResponse,
    dialog: ErrorDialogWrapperService,
    login: LoginService
  ): Observable<HttpEvent<any>> => {
    const status = error?.status;
    let errorMessage = error?.error?.message;
    let timestamp = error?.error?.timestamp;
    if (errorMessage == null || errorMessage === '') {
      timestamp = Date.now();
      switch (status) {
        case StatusCodes.GATEWAY_TIMEOUT:
          errorMessage = 'M602';
          break;
        default:
          errorMessage = 'M603';
          break;
      }
    }
    if (status === StatusCodes.UNAUTHORIZED) {
      login.revoke();
    }
    const data: ErrorDialogData = {
      message: convertToMessageData(errorMessage),
      timestamp: timestamp,
    };
    dialog.openErrorDialog(data);
    return throwError(() => error);
  };
}

/**
 * その他(クライアント側)のHTTP通信におけるエラーコマンド
 */
class OtherErrorCommand implements ErrorCommand {
  /** 検証 */
  valid = (error: HttpErrorResponse): boolean => error?.status === 0;
  /** 実行 */
  run = (
    error: HttpErrorResponse,
    dialog: ErrorDialogWrapperService
  ): Observable<HttpEvent<any>> => {
    let message = error.error.message;
    let httpRequestStatus = error.status;
    if (message == null) {
      message = convertToMessageData('M604');
      httpRequestStatus = StatusCodes.REQUEST_TIMEOUT;
    } else if (!isMessageData(message)) {
      message = convertToMessageData(message);
    }

    let data: ErrorDialogData = {
      message: message,
      timestamp: error?.error?.timestamp ?? new Date(),
    };
    dialog.openErrorDialog(data);

    return throwError(
      () =>
        new HttpErrorResponse({
          error: error.error,
          headers: error.headers,
          status: httpRequestStatus,
          url: error.url ?? undefined,
        })
    );
  };
}

const commands: Readonly<ErrorCommand[]> = [
  new IdcsErrorCommand(),
  new IdcsAdminApiErrorCommand(),
  new ServerErrorCommand(),
  new OtherErrorCommand(),
];
